import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop, strokeDashTypes } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";

import EachEllipse from "./wrapper/EachEllipse";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class Ellipse extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawEllipse = this.handleDrawEllipse.bind(this);
    this.handleDragEllipse = this.handleDragEllipse.bind(this);
    this.handleDragEllipseComplete = this.handleDragEllipseComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType("ellipses").bind(this);

    this.state = {};
    this.nodes = [];
  }

  handleDragEllipse(index, newXYValue) {
    this.setState({
      override: {
        index,
        ...newXYValue,
      },
    });
  }

  handleDragEllipseComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { ellipses } = this.props;
      const newEllipses = ellipses.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              start: [override.x1Value, override.y1Value],
              end: [override.x2Value, override.y2Value],
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newEllipses, moreProps);
        },
      );
    }
  }

  handleDrawEllipse(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.start)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          start: current.start,
          end: xyValue,
        },
      });
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.start)) {
      this.mouseMoved = false;

      this.setState(
        {
          current: {
            start: xyValue,
            end: null,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { current } = this.state;
    const { ellipses, appearance, type } = this.props;

    if (this.mouseMoved && isDefined(current) && isDefined(current.start)) {
      const currentAppearance = appearance;

      const newEllipses = [
        ...ellipses.map((d) => ({ ...d, selected: false })),
        {
          start: current.start,
          end: xyValue,
          selected: true,
          appearance: currentAppearance,
          type,
        },
      ];
      this.setState(
        {
          current: null,
        },
        () => {
          this.props.onComplete(newEllipses, moreProps, e);
        },
      );
    }
  }

  render() {
    const { appearance } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo, type } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionstrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { hoverText, ellipses } = this.props;
    const { current, override } = this.state;

    // Preview ellipse while drawing
    const tempEllipse =
      isDefined(current) && isDefined(current.end) ? (
        <EachEllipse
          interactive={false}
          x1Value={current.start[0]}
          y1Value={current.start[1]}
          x2Value={current.end[0]}
          y2Value={current.end[1]}
          appearance={appearance}
        />
      ) : null;

    return (
      <g>
        {ellipses.map((each, idx) => {
          // Simple deep merge of appearance objects
          const finalAppearance = each.appearance
            ? {
                stroke: { ...appearance.stroke, ...each.appearance.stroke },
                fill: { ...appearance.fill, ...each.appearance.fill },
              }
            : appearance;

          const hoverTextWithDefault = {
            ...Ellipse.defaultProps.hoverText,
            ...hoverText,
          };

          return (
            <EachEllipse
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              type={each.type}
              selected={each.selected}
              x1Value={getValueFromOverride(
                override,
                idx,
                "x1Value",
                each.start[0],
              )}
              y1Value={getValueFromOverride(
                override,
                idx,
                "y1Value",
                each.start[1],
              )}
              x2Value={getValueFromOverride(
                override,
                idx,
                "x2Value",
                each.end[0],
              )}
              y2Value={getValueFromOverride(
                override,
                idx,
                "y2Value",
                each.end[1],
              )}
              appearance={finalAppearance}
              r={8}
              edgeFill="#FFFFFF"
              edgeStroke="#000000"
              onDrag={this.handleDragEllipse}
              onDragComplete={this.handleDragEllipseComplete}
              hoverText={hoverTextWithDefault}
            />
          );
        })}
        {tempEllipse}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEnd}
          onMouseMove={this.handleDrawEllipse}
          data-evo-id="interactive-ellipse"
        />
      </g>
    );
  }
}

Ellipse.propTypes = {
  snap: PropTypes.bool,
  enabled: PropTypes.bool.isRequired,
  snapTo: PropTypes.func,
  shouldDisableSnap: PropTypes.func.isRequired,

  onStart: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionstrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,
  type: PropTypes.string,

  hoverText: PropTypes.object,
  ellipses: PropTypes.array.isRequired,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
        dash: PropTypes.string,
      }),
    ]),
    fill: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        opacity: PropTypes.number,
      }),
    ]),
  }).isRequired,
};

Ellipse.defaultProps = {
  type: "XLINE",
  onStart: noop,
  onComplete: noop,
  onSelect: noop,
  shouldDisableSnap: () => true,

  currentPositionStroke: "#000000",
  currentPositionOpacity: 1,
  currentPositionStrokeWidth: 3,
  currentPositionRadius: 4,
  hoverText: {
    enable: false,
  },
  ellipses: [],

  appearance: {
    stroke: {
      color: "#2196F3",
      width: 2,
      opacity: 1,
      dash: "Solid",
    },
    fill: {
      color: "#2196F3",
      opacity: 0.2,
    },
  },
};

export default Ellipse;
